import { FC, useEffect, useRef, useState } from 'react';

interface AnimationProps {
  show: boolean;
  children: React.ReactNode;
  from?: Keyframe; // Initial animation styles
  to?: Keyframe; // Final animation styles
  unMountAnimation?: Keyframe | Keyframe[]; // Animation for unmounting
  options?: Parameters<Animatable['animate']>['1'];
}
/**
 * @example:
 * export const ParentComponent = ({ renderProblem = false }) => {
 * const [show, setShow] = useState(true);
 *
 * const Wrapper = renderProblem ? Fragment : AnimationWrapper;
 *
 * return (
 *   <>
 *    <button onClick={() => setShow((state) => !state)}>Click me</button>
 *    <Wrapper show={show}>
 *      <input className={renderProblem ? "fade-animation" : ""} />
 *    </Wrapper>
 *  </>
 * );
};
 */
const AnimationWrapper: FC<AnimationProps> = ({
  show,
  children,
  from = { opacity: 0 },
  to = { opacity: 1 },
  unMountAnimation,
  options = { duration: 300, fill: 'forwards' },
}) => {
  const elementRef = useRef<HTMLElement>();

  const [removeState, setRemove] = useState(!show);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    if (show) {
      setRemove(false);
      const animation = element.animate([from, to], options);

      return () => animation.cancel();
    } else {
      const animation = element.animate(unMountAnimation || [to, from], options);
      animation.onfinish = () => {
        setRemove(true);
      };
      return () => animation.cancel();
    }
  }, [show, removeState, from, to, options, unMountAnimation]);

  return !removeState && <span ref={elementRef}>{children}</span>;
};

export default AnimationWrapper;
